import { Overlay } from '@angular/cdk/overlay';
import { Injectable, OnDestroy, Signal, TemplateRef, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { injectRegisterIcons } from '@ngneat/svg-icon';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, combineLatest, isObservable, of, switchMap } from 'rxjs';

import { RouteSegment } from '@core/enums/route-segment.enum';
import { BreadcrumbsService } from '@core/services/breadcrumbs.service';
import { BreakpointService } from '@core/services/breakpoint.service';
import { ImpersonationService } from '@core/services/impersonation.service';
import { logout } from '@core/stores/auth/auth.actions';
import { selectHeaderMode, selectImpersonationToken, selectName, selectOriginalName } from '@core/stores/auth/auth.selectors';

import { DialogService } from '@shared/modules/dialog';

import { appDashboard16Icon } from '@icons/dashboard-16';

import { BreadcrumbItemViewModel, BreadcrumbsViewModel } from './components/breadcrumbs';
import { HeaderProfileSectionViewModel } from './components/header-profile-section';
import { selectSideMenuViewModel } from './layout.selectors';
import { setSideMenuExpanded } from './store/layout.actions';

interface CustomTitleTemplate {
  template: TemplateRef<unknown>;
  includesSubtitle: boolean;
}

@Injectable()
export class LayoutService implements OnDestroy {
  readonly #store = inject(Store);
  readonly #breadcrumbsService = inject(BreadcrumbsService);
  readonly #translateService = inject(TranslateService);
  readonly #breakpointService = inject(BreakpointService);
  readonly #dialogService = inject(DialogService);
  readonly #overlay = inject(Overlay);
  readonly #impersonationService = inject(ImpersonationService);

  public customTitle$Signal = signal<Observable<string> | null>(null);

  public customSubtitle$Signal = signal<Observable<string> | null>(null);
  public customSubtitleIsLoadingSignal = signal<Signal<boolean> | null>(null);

  public customTitleTemplateArraySignal = signal<CustomTitleTemplate[]>([]);

  public customTitleTemplateSignal = computed(() => {
    const templates = this.customTitleTemplateArraySignal();
    return templates.length > 0 ? templates.at(-1) : null;
  });

  private readonly breadcrumbItem: BreadcrumbItemViewModel = {
    route: [RouteSegment.Root],
    name$: this.#translateService.stream('side-menu.dashboard'),
    icon: 'dashboard-16',
  };

  private readonly name = this.#store.selectSignal(selectName);
  private readonly originalName = this.#store.selectSignal(selectOriginalName);
  private readonly impersonationToken = this.#store.selectSignal(selectImpersonationToken);

  public readonly sideMenuVm = this.#store.selectSignal(selectSideMenuViewModel);
  public readonly headerProfileVm = computed((): HeaderProfileSectionViewModel => {
    const nameValue = this.name();

    return {
      name: nameValue ?? '',
      originalName: this.impersonationToken() ? (this.originalName() ?? undefined) : undefined,
    };
  });
  private readonly headerMode = this.#store.selectSignal(selectHeaderMode);
  private readonly breadcrumbs = toSignal(
    this.#breadcrumbsService.items$.pipe(
      switchMap((items) => {
        const itemObservables = items.map((item$): Observable<BreadcrumbItemViewModel> => {
          return isObservable(item$) ? item$ : of(item$);
        });

        if (itemObservables.length === 0) {
          return of([]);
        }

        return combineLatest(itemObservables);
      })
    ),
    {
      requireSync: true,
    }
  );
  public readonly breadcrumbsVm = computed((): BreadcrumbsViewModel => {
    const headerModeValue = this.headerMode();
    const breadcrumbsValue = this.breadcrumbs();

    return {
      darkMode: headerModeValue === 'line',
      items: breadcrumbsValue,
    };
  });
  public readonly title$Signal = computed((): Observable<string> => {
    const breadcrumbsVm = this.breadcrumbsVm();
    const customTitle$ = this.customTitle$Signal();

    if (customTitle$) {
      return customTitle$;
    }
    return breadcrumbsVm.items.at(-1)!.name$;
  });

  constructor() {
    injectRegisterIcons(appDashboard16Icon);
    this.#breadcrumbsService.addItem(this.breadcrumbItem);
  }

  public ngOnDestroy(): void {
    this.#breadcrumbsService.removeItem(this.breadcrumbItem);
  }

  public setCustomTitleTemplate(template: TemplateRef<unknown>, includesSubtitle = false): void {
    this.customTitleTemplateArraySignal.update((templates) => [...templates, { template, includesSubtitle }]);
  }

  public clearCustomTitleTemplate(template: TemplateRef<unknown>): void {
    this.customTitleTemplateArraySignal.update((templates) => templates.filter((item) => item.template !== template));
  }

  public setCustomTitle(title$: Observable<string> | null): void {
    this.customTitle$Signal.set(title$);
  }

  public clearCustomTitle(): void {
    this.customTitle$Signal.set(null);
  }

  public setCustomSubtitle(subtitle$: Observable<string> | null, isLoading?: Signal<boolean>): void {
    this.customSubtitle$Signal.set(subtitle$);
    this.customSubtitleIsLoadingSignal.set(isLoading ?? null);
  }

  public clearCustomSubtitle(): void {
    this.customSubtitle$Signal.set(null);
    this.customSubtitleIsLoadingSignal.set(null);
  }

  public onSideMenuExpandChange(expanded: boolean): void {
    this.#store.dispatch(setSideMenuExpanded({ expanded }));
  }

  public onLogoutClick(): void {
    if (this.#breakpointService.lg) {
      this.#store.dispatch(logout());
    } else {
      this.#dialogService
        .openConfirmationDialog(
          {
            title$: this.#translateService.stream('header-profile-section.logout-confirmation-dialog.title'),
            message$: this.#translateService.stream('header-profile-section.logout-confirmation-dialog.message'),
          },
          {
            positionStrategy: this.#overlay.position().global().centerHorizontally().top('12px'),
            width: 'calc(100% - 16px)',
            maxHeight: 'calc(100% - 24px)',
            disableClose: true,
          }
        )
        .afterDismissed()
        .subscribe((confirmed) => {
          if (confirmed) {
            this.#store.dispatch(logout());
          }
        });
    }
  }

  public onExitImpersonationClick(): void {
    this.#impersonationService.exit();
  }
}
